import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { descargar, imprimirPdf } from "@/lib/download";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import InlineStatus from "@/components/InlineStatus";
import EditableNote from "@/components/EditableNote";
import { PrioBadge } from "@/components/PrioridadToggle";
import { FABRICACION } from "@/lib/statuses";
import { CosturaSelect } from "@/components/CosturaSelect";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, Printer } from "lucide-react";

const FotosCelda = ({ fotos = [], id }) => (
  <div className="flex flex-wrap gap-1 min-w-[96px] max-w-[220px]" data-testid={`fabricado-fotos-${id}`}>
    {fotos.length === 0 && <span className="text-[11px] text-zinc-400">Sin foto</span>}
    {fotos.map((src, i) => (
      <a key={i} href={src} target="_blank" rel="noreferrer">
        <img src={src} alt={`foto ${i + 1}`} loading="lazy" className="w-20 h-20 object-cover rounded border border-zinc-200" />
      </a>
    ))}
  </div>
);

const hoyGT = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Guatemala" });

// Hoja "Fabricados del día" compartida: fuente = "costuras" | "fabricacion".
export const FabricadosDelDia = ({ fuente, canEdit }) => {
  const [fecha, setFecha] = useState(hoyGT());
  const [rows, setRows] = useState([]);
  const load = async () => {
    try {
      const { data } = fuente === "costuras"
        ? await api.get("/costuras", { params: { vista: "listos", fecha } })
        : await api.get("/fabricacion/fabricados", { params: { fecha } });
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => {
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fecha, fuente]);

  const run = (fn) => fn().catch((e) => toast.error(String(e.message || e)));
  const path = (fmt) => `/fabricados/${fuente}.${fmt}?fecha=${fecha}`;
  const piezas = rows.reduce((n, r) => n + r.cantidad, 0);
  const updNotas = (oid, v) => setRows((p) => p.map((x) => (x.order_id === oid ? { ...x, notas_hoja: v } : x)));

  return (
    <div className="space-y-3" data-testid={`fabricados-${fuente}`}>
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Fecha</div>
          <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value || hoyGT())} className="h-9 w-44" data-testid={`fabricados-${fuente}-fecha`} />
        </div>
        <div className="text-sm text-zinc-600 pb-2" data-testid={`fabricados-${fuente}-total`}>{rows.length} producto(s) · <b>{piezas}</b> pieza(s)</div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" className="h-9 gap-1" onClick={() => run(() => imprimirPdf(path("pdf")))} data-testid={`fabricados-${fuente}-print`}><Printer className="w-4 h-4" /> Imprimir</Button>
          <Button variant="outline" className="h-9 gap-1" onClick={() => run(() => descargar(path("xlsx"), `fabricados_${fuente}_${fecha}.xlsx`))} data-testid={`fabricados-${fuente}-xlsx`}><FileSpreadsheet className="w-4 h-4" /> Excel</Button>
          <Button variant="outline" className="h-9 gap-1" onClick={() => run(() => descargar(path("pdf"), `fabricados_${fuente}_${fecha}.pdf`))} data-testid={`fabricados-${fuente}-pdf`}><FileText className="w-4 h-4" /> PDF</Button>
        </div>
      </div>
      <div className="overflow-x-auto border border-zinc-200 rounded-md bg-white">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>{["Pedido", "Cliente", "Producto", "Estilo", "Tamaño", "Color", "Tela", "Cant.", "Listo", "Notas", ...(fuente === "fabricacion" ? ["Fotos"] : []), "Estado"].map((h) => <th key={h} className="px-3 py-2 text-left">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {rows.length === 0 && <tr><td colSpan={12} className="text-center text-zinc-400 py-8">Sin productos listos en esta fecha.</td></tr>}
            {rows.map((r) => (
              <tr key={`${r.order_id}-${r.key}`} className={`align-top ${r.prioridad ? "bg-red-100" : ""}`} data-testid={`fabricado-${fuente}-${r.numero}`}>
                <td className="px-3 py-2 font-mono text-xs font-semibold whitespace-nowrap">{r.numero}<div><PrioBadge show={r.prioridad} /></div></td>
                <td className="px-3 py-2 text-xs">{r.cliente}</td>
                <td className="px-3 py-2 text-xs font-semibold">{r.producto}</td>
                <td className="px-3 py-2 text-xs">{r.estilo}</td>
                <td className="px-3 py-2 text-xs">{r.tamano}</td>
                <td className="px-3 py-2 text-xs">{r.color}</td>
                <td className="px-3 py-2 text-xs">{r.tela}</td>
                <td className="px-3 py-2 text-xs font-bold">{r.cantidad}</td>
                <td className="px-3 py-2 text-[11px] whitespace-nowrap">{r.listo_at}</td>
                <td className="px-3 py-2 min-w-[180px]">{r.nota_pedido && <div className="text-[11px] bg-yellow-50 border border-yellow-200 rounded p-1.5 mb-1">Pedido: {r.nota_pedido}</div>}<EditableNote orderId={r.order_id} modulo={fuente === "costuras" ? "notas_cos" : "notas_fab"} initial={r.notas_hoja} onSaved={(v) => updNotas(r.order_id, v)} /></td>
                {fuente === "fabricacion" && <td className="px-3 py-2"><FotosCelda fotos={r.fotos} id={`${r.numero}-${r.key}`} /></td>}
                <td className="px-3 py-2">
                  {fuente === "costuras"
                    ? <CosturaSelect row={r} canEdit={canEdit} onChanged={load} />
                    : <InlineStatus orderId={r.order_id} campo="estado_fabricacion" value="listo" options={FABRICACION} canEdit={canEdit} onChanged={load} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
